import { useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import {
  UniversalFilterCompanyStatusStatus,
  UniversalFilterCompanyStatusValue,
  UniversalFilterCompanyStatusModes,
  UniversalFilterCompanyStatusFilterState,
  UniversalFilterCompanyStatusOnChange
} from '../../UniversalFilterCompanyStatus.types';

interface UniversalFilterCompanyStatusFilterStateOptions {
  onChange: UniversalFilterCompanyStatusOnChange;
}

function useUniversalFilterCompanyStatusFilterState({
  onChange
}: UniversalFilterCompanyStatusFilterStateOptions) {
  // All modes and options available in handleApplyFilterState
  // must be implemented in handleParseFilterValue too
  const handleApplyFilterState = useCallback<
    (filterState: UniversalFilterCompanyStatusFilterState) => void
  >(
    (filterState) => {
      const statuses = filterState?.statuses;

      if (filterState?.mode === UniversalFilterCompanyStatusModes.IS_ANY_OF) {
        onChange(isEmpty(statuses) ? undefined : { in: statuses });
        return;
      }

      if (filterState?.mode === UniversalFilterCompanyStatusModes.IS_NONE_OF) {
        onChange(isEmpty(statuses) ? undefined : { notIn: statuses });
        return;
      }

      onChange();
    },
    [onChange]
  );

  const handleParseFilterValue = useCallback<
    (
      filterValue: UniversalFilterCompanyStatusValue
    ) => UniversalFilterCompanyStatusFilterState | undefined
  >((filterValue) => {
    if (isNil(filterValue)) {
      return undefined;
    }

    if (!isEmpty(filterValue.in)) {
      return {
        mode: UniversalFilterCompanyStatusModes.IS_ANY_OF,
        statuses: filterValue.in as UniversalFilterCompanyStatusStatus[]
      };
    }

    if (!isEmpty(filterValue.notIn)) {
      return {
        mode: UniversalFilterCompanyStatusModes.IS_NONE_OF,
        statuses: filterValue.notIn as UniversalFilterCompanyStatusStatus[]
      };
    }

    return undefined;
  }, []);

  return { handleApplyFilterState, handleParseFilterValue };
}

export default useUniversalFilterCompanyStatusFilterState;
